import React, { useState, useEffect } from 'react';
import {
  login, loginWithPasskey, registerPasskey,
  passkeySupported, deviceHasPasskey, validateSession,
  verifyMFA, submitBackupCode,
  setupBeginMfa, confirmSetupMfa,
} from '../services/auth';
import QRCode from 'qrcode';
import logo from '../logo-cp.svg';

export default function Login({ onLogin }) {
  const [view, setView] = useState('loading'); // loading | passkey | password | setup | mfa
  const [setupCode, setSetupCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // PR 1c — MFA prompt state
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRemember, setMfaRemember] = useState(false);
  const [mfaBackupMode, setMfaBackupMode] = useState(false);
  // Mandatory-MFA setup state (user logged in via password but has no MFA enrolled).
  const [setupToken, setSetupToken] = useState(null);
  const [setupQrUrl, setSetupQrUrl] = useState(null);
  const [setupSecret, setSetupSecret] = useState(null);
  const [setupCodeInput, setSetupCodeInput] = useState('');
  const [setupBackupCodes, setSetupBackupCodes] = useState(null);
  // Auth 3c — forgot-password flow state.
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // On mount: check if existing session is still valid, then check for passkey support
  useEffect(() => {
    (async () => {
      // Check if current session is still valid (within 6-day window)
      const valid = await validateSession();
      if (valid) {
        const { getCurrentUser } = await import('../services/auth');
        onLogin(getCurrentUser());
        return;
      }
      // Check for ?setup=CODE in URL (from email link)
      const urlParams = new URLSearchParams(window.location.search);
      const codeFromUrl = urlParams.get('setup');
      if (codeFromUrl) {
        setSetupCode(codeFromUrl.toUpperCase());
        // Clean the URL so the code isn't visible / bookmarkable
        window.history.replaceState({}, '', window.location.pathname);
        setView('setup');
        return;
      }
      // Auth 3c — ?reset=TOKEN from password-reset email.
      const resetFromUrl = urlParams.get('reset');
      if (resetFromUrl) {
        setResetToken(resetFromUrl);
        window.history.replaceState({}, '', window.location.pathname);
        setView('reset-password');
        return;
      }
      // Only open with the passkey button on a device that has actually
      // enrolled one. A browser with no credential throws NotAllowedError,
      // which reads to a non-technical user as "my account is broken".
      if (passkeySupported() && deviceHasPasskey()) {
        setView('passkey');
      } else {
        setView('password');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePasskeyLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const user = await loginWithPasskey();
      onLogin(user);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        // WebAuthn gives the same error whether the user cancelled or the device
        // held no credential to offer, so the copy names both and points at the
        // password button rather than sending them to their admin.
        setError('Passkey sign-in did not finish - either it was cancelled, or this device has no passkey for CPx. Use the password button below.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setView('mfa');
      } else if (result?.mfaSetupRequired) {
        // Mandatory MFA — user has no MFA yet. Kick off enrollment in-line.
        setSetupToken(result.setupToken);
        setView('mfa-setup');
        try {
          const data = await setupBeginMfa(result.setupToken);
          setSetupSecret(data.secretBase32);
          const qr = await QRCode.toDataURL(data.otpauthUri, { width: 220, margin: 1 });
          setSetupQrUrl(qr);
        } catch (e) { setError(e.message); }
      } else {
        onLogin(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // PR 1c — MFA verification handler.
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = mfaBackupMode
        ? await submitBackupCode(mfaToken, mfaCode)
        : await verifyMFA(mfaToken, mfaCode, mfaRemember);
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Mandatory-MFA setup confirmation. On success, server returns session + backup codes;
  // we display the codes then proceed to onLogin.
  const handleSetupConfirm = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await confirmSetupMfa(setupToken, setupCodeInput);
      // Show backup codes once, then continue to app via "I've saved them" button.
      setSetupBackupCodes(result.backupCodes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auth 3c — forgot-password handlers.
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      await fetch(`${base}/neon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: 'auth_request_password_reset', params: { email: forgotEmail } }),
      });
      setView('forgot-sent');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (resetPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (resetPassword !== resetPasswordConfirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
      const r = await fetch(`${base}/neon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: 'auth_complete_password_reset', params: { token: resetToken, new_password: resetPassword } }),
      });
      const data = await r.json();
      if (data?.error) { setError(data.error); return; }
      setResetSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSetupPasskey = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await registerPasskey(setupCode);
      onLogin(user);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey setup was cancelled. Try again.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Detect biometric type for button label ─────────────────────────────────
  const getBiometricLabel = () => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return { icon: '\uD83D\uDE4D', label: 'Sign in with Face ID' };
    if (/Android/.test(ua)) return { icon: '\uD83D\uDD13', label: 'Sign in with Biometrics' };
    if (/Mac/.test(ua)) return { icon: '\uD83D\uDD13', label: 'Sign in with Touch ID' };
    if (/Windows/.test(ua)) return { icon: '\uD83D\uDD13', label: 'Sign in with Windows Hello' };
    return { icon: '\uD83D\uDD11', label: 'Sign in with Passkey' };
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'Montserrat, sans-serif',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const btnStyle = (primary) => ({
    width: '100%',
    padding: '11px',
    background: primary ? 'var(--orange, #DC582A)' : 'var(--navy, #00395C)',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'Montserrat, sans-serif',
    cursor: loading ? 'default' : 'pointer',
    letterSpacing: '0.5px',
    opacity: loading ? 0.6 : 1,
  });

  const linkStyle = {
    background: 'none', border: 'none', color: '#6b7280', fontSize: 11,
    cursor: 'pointer', marginTop: 16, display: 'block', textAlign: 'center',
    fontFamily: 'Montserrat, sans-serif', textDecoration: 'underline',
  };

  const bio = getBiometricLabel();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--navy, #00395C)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: '40px 48px',
        width: '100%',
        maxWidth: 400,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={logo} alt="Crab Place" style={{ height: 48, marginBottom: 16 }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy, #00395C)' }}>Admin Portal</div>
        </div>

        {error && (
          <div style={{
            marginBottom: 16, padding: '10px 12px',
            background: '#fff5f5', border: '1px solid #fcc',
            borderRadius: 6, fontSize: 12, color: '#c0392b', fontWeight: 600,
          }}>
            {error}
          </div>
        )}

        {/* ── Loading state ── */}
        {view === 'loading' && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280', fontSize: 13 }}>
            Checking session...
          </div>
        )}

        {/* ── Passkey login (primary) ── */}
        {view === 'passkey' && (
          <div>
            <button
              onClick={handlePasskeyLogin}
              disabled={loading}
              style={{ ...btnStyle(true), fontSize: 14, padding: '14px' }}
            >
              {loading ? 'Verifying...' : `${bio.icon}  ${bio.label}`}
            </button>

            <button
              type="button"
              onClick={() => { setError(null); setView('password'); }}
              style={{ ...btnStyle(false), marginTop: 10 }}
            >
              Use my password instead
            </button>

            <button style={linkStyle} onClick={() => setView('setup')}>
              Set up new device
            </button>
          </div>
        )}

        {/* ── Password login (fallback) ── */}
        {view === 'password' && (
          <form onSubmit={handlePasswordLogin}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: '#9ca3af', fontSize: 16, lineHeight: 1,
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '\uD83D\uDE48' : '\uD83D\uDC41'}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} style={btnStyle(true)}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button type="button" style={linkStyle} onClick={() => { setError(null); setView('forgot'); }}>
              Forgot your password?
            </button>

            {passkeySupported() && (
              <button
                type="button"
                onClick={() => { setError(null); setView('passkey'); }}
                style={{ ...btnStyle(false), marginTop: 10 }}
              >
                {bio.icon}  {bio.label} instead
              </button>
            )}

            {passkeySupported() && (
              <button type="button" style={linkStyle} onClick={() => { setError(null); setView('setup'); }}>
                Set up passkey for this device
              </button>
            )}
          </form>
        )}

        {/* ── PR 1c MFA prompt ── */}
        {view === 'mfa' && (
          <form onSubmit={handleMfaVerify}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{'\uD83D\uDD12'}</div>
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                {mfaBackupMode
                  ? 'Enter one of the 8-character backup codes you saved when you set up two-factor authentication.'
                  : 'Enter the 6-digit code from your authenticator app (Authy, Google Authenticator, 1Password, etc.).'}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>
                {mfaBackupMode ? 'Backup Code' : 'Authentication Code'}
              </label>
              <input
                type="text"
                value={mfaCode}
                onChange={e => setMfaCode(mfaBackupMode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                placeholder={mfaBackupMode ? 'XXXX-XXXX' : '123456'}
                inputMode={mfaBackupMode ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                style={{ ...inputStyle, textAlign: 'center', fontSize: 22, letterSpacing: mfaBackupMode ? 3 : 8, fontWeight: 700, fontFamily: 'monospace' }}
              />
            </div>
            {!mfaBackupMode && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--navy, #00395C)', cursor: 'pointer', marginBottom: 16 }}>
                <input type="checkbox" checked={mfaRemember} onChange={e => setMfaRemember(e.target.checked)} />
                Remember this browser for 7 days
              </label>
            )}
            <button type="submit" disabled={loading || (mfaBackupMode ? mfaCode.length < 9 : mfaCode.length < 6)} style={btnStyle(true)}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button type="button" style={linkStyle} onClick={() => { setError(null); setMfaCode(''); setMfaBackupMode(!mfaBackupMode); }}>
              {mfaBackupMode ? 'Use authenticator app instead' : 'Use a backup code instead'}
            </button>
          </form>
        )}

        {/* Mandatory-MFA: forced-enrollment view shown after password verify for users without MFA. */}
        {view === 'mfa-setup' && !setupBackupCodes && (
          <form onSubmit={handleSetupConfirm}>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>Set up two-factor authentication</div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                Password sign-in now requires two-factor authentication. Install an authenticator app (Authy, Google Authenticator, 1Password), scan the QR below, then enter the 6-digit code your app shows.
              </div>
            </div>
            {setupQrUrl ? (
              <div style={{ textAlign: 'center', margin: '12px 0' }}>
                <img src={setupQrUrl} alt="MFA QR code" style={{ border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
            ) : <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Rendering QR...</div>}
            {setupSecret && (
              <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', marginBottom: 12, fontFamily: 'monospace', wordBreak: 'break-all', padding: '6px', background: '#f9fafb', borderRadius: 4 }}>
                Can't scan? Enter this code manually: {setupSecret}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>Enter the 6-digit code from your app:</label>
            <input
              type="text"
              value={setupCodeInput}
              onChange={e => setSetupCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              style={{ ...inputStyle, textAlign: 'center', fontSize: 22, letterSpacing: 8, fontWeight: 700, fontFamily: 'monospace' }}
            />
            <button type="submit" disabled={loading || setupCodeInput.length < 6} style={{ ...btnStyle(true), marginTop: 12 }}>
              {loading ? 'Verifying...' : 'Confirm and Sign In'}
            </button>
          </form>
        )}

        {/* Mandatory-MFA: backup-codes display after successful setup confirm. */}
        {view === 'mfa-setup' && setupBackupCodes && (
          <div>
            <div style={{ background: '#ecfdf5', border: '1px solid #10b981', borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 13, color: '#065f46', fontWeight: 700 }}>
              Two-factor authentication enabled. Save these 8 backup codes — each works once if you lose your authenticator. They will not be shown again.
            </div>
            <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8, marginBottom: 12 }}>
              {setupBackupCodes.map(c => <div key={c}>{c}</div>)}
            </div>
            <button type="button" onClick={() => { navigator.clipboard.writeText(setupBackupCodes.join('\n')); }} style={{ ...btnStyle(false), marginBottom: 8 }}>
              Copy all backup codes
            </button>
            <button type="button" onClick={() => { const user = JSON.parse(localStorage.getItem('currentUser')); onLogin(user); }} style={btnStyle(true)}>
              I've saved them — continue to the dashboard
            </button>
          </div>
        )}

                {/* Auth 3c — forgot-password email entry */}
        {view === 'forgot' && (
          <form onSubmit={handleForgotSubmit}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>Reset your password</div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                Enter the email address on your CPx Admin account. We'll send you a reset link.
              </div>
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>Email</label>
            <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required autoFocus autoComplete="email" style={inputStyle} />
            <button type="submit" disabled={loading || !forgotEmail} style={{ ...btnStyle(true), marginTop: 12 }}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <button type="button" style={linkStyle} onClick={() => { setError(null); setView('password'); }}>
              Back to sign in
            </button>
          </form>
        )}

        {/* Auth 3c — generic confirmation (intentionally same response whether email exists or not) */}
        {view === 'forgot-sent' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{'\u2709\uFE0F'}</div>
            <div style={{ fontSize: 14, color: 'var(--navy, #00395C)', fontWeight: 700, marginBottom: 8 }}>Check your inbox</div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 16 }}>
              If an active CPx Admin account exists for that email, we sent a reset link. The link expires in 30 minutes.
            </div>
            <button type="button" style={btnStyle(true)} onClick={() => { setError(null); setView('password'); }}>Back to sign in</button>
          </div>
        )}

        {/* Auth 3c — password reset form (entered via ?reset=TOKEN) */}
        {view === 'reset-password' && !resetSuccess && (
          <form onSubmit={handleResetSubmit}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>Set a new password</div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                Pick at least 12 characters that don't include common words. After saving, all existing sessions are signed out — you'll need to log in fresh.
              </div>
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>New password</label>
            <input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} required autoFocus autoComplete="new-password" style={inputStyle} />
            {resetPassword && (
              <div style={{ marginTop: 6, fontSize: 10, lineHeight: 1.5 }}>
                <div style={{ color: resetPassword.length >= 12 ? '#16a34a' : 'var(--gray-500, #6b7280)' }}>
                  {resetPassword.length >= 12 ? '✓' : '○'} At least 12 characters
                </div>
                {resetPasswordConfirm.length > 0 && (
                  <div style={{ color: resetPassword === resetPasswordConfirm ? '#16a34a' : 'var(--gray-500, #6b7280)' }}>
                    {resetPassword === resetPasswordConfirm ? '✓' : '○'} Confirmation matches
                  </div>
                )}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6, marginTop: 12 }}>Confirm new password</label>
            <input type="password" value={resetPasswordConfirm} onChange={e => setResetPasswordConfirm(e.target.value)} required autoComplete="new-password" style={inputStyle} />
            <button type="submit" disabled={loading || resetPassword.length < 12 || resetPassword !== resetPasswordConfirm} style={{ ...btnStyle(true), marginTop: 12 }}>
              {loading ? 'Saving...' : 'Set new password'}
            </button>
          </form>
        )}

        {/* Auth 3c — reset success */}
        {view === 'reset-password' && resetSuccess && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{'\u2705'}</div>
            <div style={{ fontSize: 14, color: 'var(--navy, #00395C)', fontWeight: 700, marginBottom: 8 }}>Password updated</div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 16 }}>
              Your password has been changed and all active sessions have been signed out. Sign in with your new password.
            </div>
            <button type="button" style={btnStyle(true)} onClick={() => { setError(null); setView('password'); setResetPassword(''); setResetPasswordConfirm(''); setResetSuccess(false); setResetToken(null); }}>Go to sign in</button>
          </div>
        )}

        {/* ── Setup new passkey with code ── */}
        {view === 'setup' && (
          <form onSubmit={handleSetupPasskey}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>{bio.icon}</div>
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                Enter the setup code from your admin to register {bio.label.replace('Sign in with ', '')} on this device.
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>
                Setup Code
              </label>
              <input
                type="text"
                value={setupCode}
                onChange={e => setSetupCode(e.target.value.toUpperCase())}
                required
                autoFocus
                placeholder="e.g. A3F7B2C1"
                maxLength={8}
                autoComplete="off"
                style={{ ...inputStyle, textAlign: 'center', fontSize: 18, letterSpacing: 4, fontWeight: 700 }}
              />
            </div>

            <button type="submit" disabled={loading || setupCode.length < 6} style={btnStyle(true)}>
              {loading ? 'Setting up...' : `Register ${bio.label.replace('Sign in with ', '')}`}
            </button>

            <button type="button" style={linkStyle} onClick={() => { setError(null); setView('password'); }}>
              Use password instead
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
